import React, { useEffect, useState } from "react";
import { authStore } from "@/lib/authStore";
import {
  Tag, Loader2, RefreshCw, Save, ArrowRight, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VENDORS = ["Huawei", "Nokia", "Ericsson", "Samsung", "Mavenir", "ZTE", "Cisco", "Juniper", "Other"];
const DOMAINS = ["RAN", "Core", "Transport", "OSS", "BSS", "Cloud", "Other"];
const DOCTYPES = ["SOP", "Manual", "Spec", "Training", "Report", "KPI", "Other"];

function Field({ label, value, onChange, options, placeholder, testid }) {
  return (
    <select
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs border rounded-lg px-2 py-1 bg-background"
    >
      <option value="">{placeholder || `— ${label} —`}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function CorpusRetagPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staging, setStaging] = useState(null);
  const [savingDocId, setSavingDocId] = useState(null);
  const [editState, setEditState] = useState({});
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const token = authStore.getAccessToken();
      const [u, s] = await Promise.all([
        fetch(`${API}/admin/corpus-health/untagged-docs?limit=50`, {
          headers: { "Authorization": `Bearer ${token}` },
        }).then(r => r.json()),
        fetch(`${API}/admin/corpus-health/staging-duplicates`, {
          headers: { "Authorization": `Bearer ${token}` },
        }).then(r => r.json()),
      ]);
      setItems(u.items || []);
      setStaging(s);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function save(docId) {
    const edit = editState[docId];
    if (!edit?.vendor) { alert("Pick a vendor first."); return; }
    setSavingDocId(docId);
    try {
      const token = authStore.getAccessToken();
      const csrf  = authStore.getCsrfToken();
      const res = await fetch(`${API}/admin/corpus-health/retag`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({
          doc_id: docId,
          vendor: edit.vendor,
          domain: edit.domain || undefined,
          doc_type: edit.doc_type || undefined,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      const body = await res.json();
      setItems((prev) => prev.filter(i => i.doc_id !== docId));
      console.log("[RETAG_OK]", body);
    } catch (e) {
      alert(`Retag failed: ${e.message}`);
    } finally {
      setSavingDocId(null);
    }
  }

  async function runDedupe(dry_run) {
    if (!dry_run && !confirm(
      "Move all duplicate staging blobs to the archive prefix? They can be restored from /archive/ if needed."
    )) return;
    try {
      const token = authStore.getAccessToken();
      const csrf  = authStore.getCsrfToken();
      const res = await fetch(`${API}/admin/corpus-health/staging-dedupe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ dry_run, limit: 500 }),
      });
      const body = await res.json();
      alert(
        `Dedupe ${dry_run ? "DRY-RUN" : "APPLIED"}\n` +
        `Moved: ${body.moved_count}, Failed: ${body.failed_count}\n` +
        (body.moved?.slice(0, 3).map(m => `${m.src} → ${m.dst}`).join("\n") || "")
      );
      load();
    } catch (e) {
      alert(`Dedupe failed: ${e.message}`);
    }
  }

  if (loading) return (
    <div className="flex items-center gap-2 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading corpus debt…
    </div>
  );
  if (error) return (
    <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-600">
      Failed to load corpus debt: {error}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="corpus-retag-panel">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Tag className="h-5 w-5 text-primary" /> Corpus Debt — Re-tag &amp; Dedupe
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Documents with empty or corrupted vendor metadata. Click into a row, pick a vendor, save.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border hover:bg-secondary text-sm"
          data-testid="corpus-retag-refresh-btn"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Staging duplicates KPI tile */}
      {staging?.available && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-amber-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Staging duplicates
            </div>
            <div className="text-3xl font-semibold tabular-nums mt-1" data-testid="staging-dupes-count">
              {staging.duplicate_count}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {Object.entries(staging.by_vendor || {}).map(([v, n]) => `${v}: ${n}`).join(" · ")}
            </div>
          </div>
          <div className="flex gap-2 self-center">
            <button
              onClick={() => runDedupe(true)}
              className="px-3 py-1.5 rounded-lg border text-sm hover:bg-secondary"
              data-testid="staging-dedupe-dry-run-btn"
            >
              Dry-run
            </button>
            <button
              onClick={() => runDedupe(false)}
              className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm"
              data-testid="staging-dedupe-apply-btn"
              disabled={staging.duplicate_count === 0}
            >
              Move to archive
            </button>
          </div>
        </div>
      )}

      {/* Untagged docs list */}
      <div className="rounded-2xl border border-border/50 bg-card">
        <div className="p-4 border-b border-border/40 flex items-center justify-between">
          <div className="font-medium">Untagged documents <span className="text-muted-foreground text-sm">({items.length})</span></div>
          {items.length === 0 && <div className="text-xs text-emerald-600">No documents need re-tagging — corpus is clean.</div>}
        </div>
        <div className="divide-y divide-border/40 max-h-[600px] overflow-y-auto">
          {items.map((it) => {
            const e = editState[it.doc_id] || {};
            return (
              <div key={it.doc_id} className="p-3 flex items-center gap-3" data-testid={`retag-row-${it.doc_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{it.filename || it.doc_id}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {it.untagged_chunks} chunks ·
                    {it.is_uuid_leak && <span className="text-red-600 ml-1">UUID-leak</span>}
                    {it.doc_status && <span className="ml-1">· {it.doc_status}</span>}
                  </div>
                </div>
                <Field
                  label="vendor" testid={`vendor-select-${it.doc_id}`}
                  value={e.vendor} options={VENDORS}
                  onChange={(v) => setEditState({ ...editState, [it.doc_id]: { ...e, vendor: v } })}
                />
                <Field
                  label="domain" testid={`domain-select-${it.doc_id}`}
                  value={e.domain} options={DOMAINS}
                  onChange={(v) => setEditState({ ...editState, [it.doc_id]: { ...e, domain: v } })}
                />
                <Field
                  label="type" testid={`doctype-select-${it.doc_id}`}
                  value={e.doc_type} options={DOCTYPES}
                  onChange={(v) => setEditState({ ...editState, [it.doc_id]: { ...e, doc_type: v } })}
                />
                <button
                  onClick={() => save(it.doc_id)}
                  disabled={savingDocId === it.doc_id || !e.vendor}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-xs disabled:opacity-50"
                  data-testid={`retag-save-${it.doc_id}`}
                >
                  {savingDocId === it.doc_id
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <Save className="h-3 w-3" />}
                  Save
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
